import {
  exportBackup,
  getEffectiveLevel,
  getUserLevelProfile,
  listAllAnnotations,
  listAllLearningSteps,
  listReadings,
  normalizeSelectedText,
} from "../db";
import {
  batchReportFilename,
  buildBatchReportMarkdown,
  downloadMarkdown,
  downloadReadingReportById,
} from "../export/report";
import { LEVEL_LABELS, levelToScore } from "../level";
import { navigate } from "../router";
import type { EnglishLevel, LevelHistoryEntry, Route } from "../types";
import { getViewEl } from "../ui/layout";
import { esc, toast } from "../ui/toast";

const STATUS_LABEL: Record<string, string> = {
  draft: "草稿",
  diagnosing: "诊断中",
  learning: "学习中",
  done: "已读懂",
};

function groupByReadingId<T extends { readingId: string }>(rows: T[]): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const row of rows) {
    const list = map.get(row.readingId) ?? [];
    list.push(row);
    map.set(row.readingId, list);
  }
  return map;
}

function resolveLevelHistory(profile: Awaited<ReturnType<typeof getUserLevelProfile>>): LevelHistoryEntry[] {
  if (profile.levelHistory?.length) {
    return profile.levelHistory;
  }
  if (profile.lastAssessedAt) {
    return [{ at: profile.lastAssessedAt, level: profile.assessedLevel }];
  }
  return [{ at: new Date().toISOString(), level: profile.assessedLevel }];
}

function renderLevelTimeline(history: LevelHistoryEntry[]): string {
  if (history.length === 0) {
    return `<p class="muted">暂无水平记录</p>`;
  }

  const width = 320;
  const height = 72;
  const padX = 12;
  const padY = 14;
  const plotW = width - padX * 2;
  const plotH = height - padY * 2;
  const sorted = [...history].sort((a, b) => a.at.localeCompare(b.at));
  const points = sorted.map((entry, i) => {
    const x = padX + (sorted.length === 1 ? plotW / 2 : (i / (sorted.length - 1)) * plotW);
    const y = padY + plotH - (levelToScore(entry.level) / 2) * plotH;
    return { ...entry, x, y };
  });

  const polyline = points.map((p) => `${p.x},${p.y}`).join(" ");
  const dots = points
    .map(
      (p) =>
        `<circle cx="${p.x}" cy="${p.y}" r="4" fill="var(--brand-2)" />` +
        `<title>${esc(LEVEL_LABELS[p.level])} · ${esc(p.at.slice(0, 10))}</title>`,
    )
    .join("");

  const labels = (["beginner", "intermediate", "advanced"] as EnglishLevel[])
    .map((level, i) => {
      const y = padY + plotH - (i / 2) * plotH;
      return `<text x="4" y="${y + 4}" class="level-timeline-label">${esc(LEVEL_LABELS[level])}</text>`;
    })
    .join("");

  return `
    <svg class="level-timeline" viewBox="0 0 ${width} ${height}" role="img" aria-label="水平变化">
      ${labels}
      <polyline points="${polyline}" fill="none" stroke="var(--brand-2)" stroke-width="2" />
      ${dots}
    </svg>
    <ul class="level-timeline-list">
      ${sorted
        .slice()
        .reverse()
        .slice(0, 5)
        .map(
          (entry) =>
            `<li><span class="badge badge-level">${esc(LEVEL_LABELS[entry.level])}</span> ${esc(entry.at.slice(0, 10))}</li>`,
        )
        .join("")}
    </ul>`;
}

function topFrequentSelections(annotations: Awaited<ReturnType<typeof listAllAnnotations>>): Array<{
  text: string;
  count: number;
}> {
  const counts = new Map<string, { text: string; count: number }>();
  for (const ann of annotations) {
    const text = normalizeSelectedText(ann.selectedText);
    if (!text || text.length > 50) {
      continue;
    }
    const key = text.toLowerCase();
    const existing = counts.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(key, { text, count: 1 });
    }
  }
  return [...counts.values()].sort((a, b) => b.count - a.count).slice(0, 10);
}

function renderBarChart(items: Array<{ label: string; value: number; className?: string }>, max: number): string {
  return `<ul class="bar-chart">
    ${items
      .map((item) => {
        const pct = max > 0 ? Math.round((item.value / max) * 100) : 0;
        return `<li class="bar-chart-row">
          <span class="bar-chart-label">${esc(item.label)}</span>
          <span class="bar-chart-track"><span class="bar-chart-fill ${item.className ?? ""}" style="width:${pct}%"></span></span>
          <span class="bar-chart-value">${item.value}</span>
        </li>`;
      })
      .join("")}
  </ul>`;
}

export async function renderStatisticsPage(_route: Route): Promise<void> {
  const view = getViewEl();
  if (!view) {
    return;
  }

  const [readings, profile, effectiveLevel, allSteps, allAnnotations] = await Promise.all([
    listReadings(),
    getUserLevelProfile(),
    getEffectiveLevel(),
    listAllLearningSteps(),
    listAllAnnotations(),
  ]);

  const statusCounts = {
    draft: readings.filter((r) => r.status === "draft").length,
    learning: readings.filter((r) => r.status === "learning" || r.status === "diagnosing").length,
    done: readings.filter((r) => r.status === "done").length,
  };
  const stepsDoneDb = allSteps.filter((s) => s.done).length;
  const stepsDoneSignal = profile.signals.stepsCompleted;
  const levelHistory = resolveLevelHistory(profile);
  const frequentWords = topFrequentSelections(allAnnotations);
  const statusMax = Math.max(statusCounts.draft, statusCounts.learning, statusCounts.done, 1);

  const recentReadings = readings.slice(0, 8);

  view.innerHTML = `
    <div class="page-head">
      <div>
        <h1>学习统计</h1>
        <p class="lead">基于本机阅读数据汇总。当前有效水平：<span class="badge badge-level">${esc(LEVEL_LABELS[effectiveLevel])}</span></p>
      </div>
      <div class="page-head-actions">
        <button type="button" class="btn btn-primary" id="btnExportAll">导出全部报告</button>
      </div>
    </div>

    <div class="stats-grid">
      <section class="panel stat-card">
        <h2>阅读篇数</h2>
        <p class="stat-number">${readings.length}</p>
        ${renderBarChart(
          [
            { label: "草稿", value: statusCounts.draft, className: "bar-draft" },
            { label: "学习中", value: statusCounts.learning, className: "bar-learning" },
            { label: "已读懂", value: statusCounts.done, className: "bar-done" },
          ],
          statusMax,
        )}
      </section>

      <section class="panel stat-card">
        <h2>学习步骤</h2>
        <p class="stat-number">${stepsDoneDb}</p>
        <p class="muted small">已完成步骤（数据库） · 信号计数 ${stepsDoneSignal}</p>
      </section>

      <section class="panel stat-card stat-card-wide">
        <h2>水平评估</h2>
        <p><span class="badge badge-level">${esc(LEVEL_LABELS[profile.assessedLevel])}</span>
          置信度 ${Math.round(profile.confidence * 100)}%</p>
        ${profile.levelReason ? `<p class="muted small">${esc(profile.levelReason)}</p>` : ""}
        <dl class="signal-list">
          <div><dt>诊断完成</dt><dd>${profile.signals.diagnosesCompleted}</dd></div>
          <div><dt>步骤完成</dt><dd>${profile.signals.stepsCompleted}</dd></div>
          <div><dt>求详解</dt><dd>${profile.signals.simplifyRequests}</dd></div>
        </dl>
        <h3 class="stat-subhead">水平曲线</h3>
        ${renderLevelTimeline(levelHistory)}
      </section>

      <section class="panel stat-card stat-card-wide">
        <h2>常问的词</h2>
        ${
          frequentWords.length
            ? `<ol class="freq-word-list">
                ${frequentWords
                  .map(
                    (item) =>
                      `<li><span class="freq-word">${esc(item.text)}</span><span class="badge">${item.count}</span></li>`,
                  )
                  .join("")}
              </ol>`
            : `<p class="muted">暂无划词记录</p>`
        }
      </section>
    </div>

    <section class="panel">
      <div class="page-head" style="margin-bottom:12px">
        <h2 style="margin:0">导出单篇报告</h2>
      </div>
      ${
        recentReadings.length
          ? `<ul class="reading-list">
              ${recentReadings
                .map(
                  (item) => `
                <li class="reading-item" data-id="${esc(item.id)}">
                  <div class="reading-item-body">
                    <h3>${esc(item.title)}</h3>
                    <p class="meta">
                      <span class="badge badge-status badge-${esc(item.status)}">${esc(STATUS_LABEL[item.status] ?? item.status)}</span>
                      <span>${esc(item.updatedAt.slice(0, 10))}</span>
                    </p>
                  </div>
                  <div class="reading-item-actions">
                    <button type="button" class="btn btn-sm" data-action="open">打开</button>
                    <button type="button" class="btn btn-sm" data-action="export">导出 Markdown</button>
                  </div>
                </li>`,
                )
                .join("")}
            </ul>`
          : `<p class="muted">还没有阅读记录，先去阅读页添加内容。</p>`
      }
    </section>
  `;

  view.querySelector<HTMLButtonElement>("#btnExportAll")?.addEventListener("click", async () => {
    try {
      const data = await exportBackup();
      const blocksByReading = groupByReadingId(data.diagnosisBlocks);
      const stepsByReading = groupByReadingId(data.learningSteps);
      const annotationsByReading = groupByReadingId(data.selectionAnnotations ?? []);
      const content = buildBatchReportMarkdown(
        data.readings,
        blocksByReading,
        stepsByReading,
        annotationsByReading,
      );
      downloadMarkdown(batchReportFilename(), content);
      toast("已导出全部报告");
    } catch (err) {
      toast(err instanceof Error ? err.message : "导出失败");
    }
  });

  view.querySelectorAll<HTMLElement>(".reading-item").forEach((item) => {
    const id = item.dataset.id;
    if (!id) {
      return;
    }

    item.querySelector<HTMLButtonElement>('[data-action="open"]')?.addEventListener("click", () => {
      navigate("diagnosis", null, id);
    });

    item.querySelector<HTMLButtonElement>('[data-action="export"]')?.addEventListener("click", async () => {
      try {
        await downloadReadingReportById(id);
        toast("已导出");
      } catch (err) {
        toast(err instanceof Error ? err.message : "导出失败");
      }
    });
  });
}
